import type { UseComboboxStateChangeTypes } from 'downshift';
import { useCombobox } from 'downshift';

import type { IOptions } from '../../../shared/utils/options';

import type { IInputDropdownProps, TSearchRules } from '../_props';

/**
 * Typings
 */

type TUseSearch = {
  filterable?: boolean;
  options: IOptions[];
  isSelect?: boolean;
  searchRules: Required<TSearchRules>;
};

type THandleSearchFilter = {
  type: UseComboboxStateChangeTypes;
  inputValue?: string;
  setListOptions: (listOptions: IOptions[]) => void;
};

type TPropagateSearch = {
  selectedItem?: IOptions | null;
  inputValue?: string;
  type: UseComboboxStateChangeTypes;
} & Pick<IInputDropdownProps, 'onChangeText'>;

/**
 * State Types Permissions
 */

const searchFilterAllowedTypes = new Set<UseComboboxStateChangeTypes>([
  useCombobox.stateChangeTypes.InputKeyDownEnter,
  useCombobox.stateChangeTypes.ItemClick,
  useCombobox.stateChangeTypes.FunctionSelectItem,
  useCombobox.stateChangeTypes.InputChange,
]);

const propagateSearchAllowedTypes = new Set<UseComboboxStateChangeTypes>([
  useCombobox.stateChangeTypes.InputChange,
]);

/**
 * Hook
 */

export const useSearch = ({ filterable, options, isSelect, searchRules }: TUseSearch) => {
  /**
   * Handlers
   */

  const handleSearchFilter = ({ type, inputValue, setListOptions }: THandleSearchFilter) => {
    if (!filterable || isSelect) return;

    if (searchFilterAllowedTypes.has(type)) {
      const filteredOptions = options
        .filter((option) => option.label.toLowerCase().includes(inputValue?.toLowerCase() || ''))
        .filter((option) => searchRules.filterRule(option, inputValue))
        .sort(searchRules.sortRule(inputValue))
        .concat(searchRules.missingOptionRule(inputValue));

      setListOptions(filteredOptions);
    }
  };

  const propagateSearch = ({ type, inputValue, onChangeText }: TPropagateSearch) => {
    if (!inputValue) return;

    if (propagateSearchAllowedTypes.has(type)) {
      onChangeText?.(inputValue, 'Search');
    }
  };

  /**
   * Return
   */

  return { handleSearchFilter, propagateSearch };
};

export default useSearch;
